import React, { useContext, useEffect, useState } from 'react'
import CustomPaper from '../../containers/Paper'
import CustomTable from '../../containers/Table'
import CustomModal from '../../containers/Modal'
import TextModal from '../../components/Modal/TextModal'
import { UserDTO } from '../../models/users'
import { useSnackbar } from 'notistack'
import UserService from '../../services/UserService'
import { FetchResponse } from '../../models/shared'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import CustomIconButton from '../../components/IconButton'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RejectModal from '../../components/Modal/RejectModal'
import PendingUserForm from './PendingUserForm'
import NoResults from '../../containers/NoResults'
import { CircularProgress } from '@mui/material'
import { PageContext } from '../../context/PageContext'
import { dateFormatter } from '../../helpers/utils'

const GraduatesPage = () => {
    const [users, setUsers] = useState<UserDTO[]>()
    const [loading, setLoading] = useState<boolean>(false)
    const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false)
    const [mutate, setMutate] = useState<boolean>(false)
    const [selectedUser, setSelectedUser] = useState<UserDTO>()
    const [isApproveUserOpen, setIsApproveUserOpen] = useState<boolean>(false)
    const [isRejectUserOpen, setIsRejectUserOpen] = useState<boolean>(false)
    const { enqueueSnackbar } = useSnackbar()
    const { smallScreen } = useContext(PageContext)

    useEffect(() => {
        new UserService().getPendingUsers().then((response) => {
            setUsers(response.data)
        })
    }, [mutate])

    const handleAction = async (approve: boolean, object?: { rejectionReason: string }) => {
        let response
        let message
        let closeModal

        setLoading(true)
        if (approve) {
            response = await new UserService().approveUser(selectedUser?.id as string)
            message = 'Graduado pendiente aprobado'
            closeModal = setIsApproveUserOpen
        } else {
            response = await new UserService().rejectUser(selectedUser?.id as string, object!)
            message = 'Graduado pendiente rechazado'
            closeModal = setIsRejectUserOpen
        }
        setLoading(false)

        if ((response as FetchResponse).status === 200) {
            enqueueSnackbar(message, {
                variant: 'success',
                autoHideDuration: 6000,
            })
            setMutate(!mutate)
            closeModal(false)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    const columnHelper = createColumnHelper<UserDTO>()
    const columns: ColumnDef<UserDTO, any>[] = [
        columnHelper.accessor('email', {
            header: 'Email',
            cell: ({ getValue }) => (
                <div className="text-sm text-secondary">{getValue<string>()}</div>
            ),
        }),
    ]

    if (!smallScreen) {
        columns.push(
            columnHelper.accessor('firstName', {
                header: 'Nombre',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary">{getValue<string>()}</div>
                ),
            }),
            columnHelper.accessor('lastName', {
                header: 'Apellido',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary">{getValue<string>()}</div>
                ),
            }),
            columnHelper.accessor('dateOfBirth', {
                header: 'Fecha de nacimiento',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary">
                        {dateFormatter(new Date(getValue<string>()))}
                    </div>
                ),
            }),
            columnHelper.accessor('documentType', {
                header: 'Tipo de documento',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary">{getValue<string>()}</div>
                ),
            }),
            columnHelper.accessor('documentValue', {
                header: 'Número de documento',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary">{getValue<string>()}</div>
                ),
            }),
            columnHelper.accessor('id', {
                header: 'Acciones',
                cell: ({ row }) => (
                    <div className="flex justify-center w-[100px]">
                        <CustomIconButton
                            icon={<VisibilityIcon />}
                            onClick={() => {
                                const selected = users?.find((user) => user.id === row.original.id)
                                setSelectedUser(selected)
                                setIsUserModalOpen(true)
                            }}
                            title="Ver detalles"
                        />
                        <CustomIconButton
                            icon={<CheckCircleIcon />}
                            onClick={() => {
                                const selected = users?.find((user) => user.id === row.original.id)
                                setSelectedUser(selected)
                                setIsApproveUserOpen(true)
                            }}
                            title="Aprobar"
                            className="text-success"
                        />
                        <CustomIconButton
                            icon={<CancelIcon />}
                            onClick={() => {
                                const selected = users?.find((user) => user.id === row.original.id)
                                setSelectedUser(selected)
                                setIsRejectUserOpen(true)
                            }}
                            title="Rechazar"
                            className="text-error"
                        />
                    </div>
                ),
            }),
        )
    } else {
        columns.push(
            columnHelper.accessor('id', {
                header: 'Acciones',
                cell: ({ row }) => (
                    <div className="flex justify-center w-[100px]">
                        <CustomIconButton
                            icon={<VisibilityIcon />}
                            onClick={() => {
                                const selected = users?.find((user) => user.id === row.original.id)
                                setSelectedUser(selected)
                                setIsUserModalOpen(true)
                            }}
                            title="Ver detalles"
                        />
                        <CustomIconButton
                            icon={<CheckCircleIcon />}
                            onClick={() => {
                                const selected = users?.find((user) => user.id === row.original.id)
                                setSelectedUser(selected)
                                setIsApproveUserOpen(true)
                            }}
                            title="Aprobar"
                            className="text-success"
                        />
                        <CustomIconButton
                            icon={<CancelIcon />}
                            onClick={() => {
                                const selected = users?.find((user) => user.id === row.original.id)
                                setSelectedUser(selected)
                                setIsRejectUserOpen(true)
                            }}
                            title="Rechazar"
                            className="text-error"
                        />
                    </div>
                ),
            }),
        )
    }

    return (
        <div className="flex flex-col gap-4 w-full">
            {users ? (
                users.length > 0 ? (
                    <CustomPaper>
                        <CustomTable
                            data={users}
                            columns={columns}
                        />
                    </CustomPaper>
                ) : (
                    <CustomPaper>
                        <NoResults
                            title="No hay graduados pendientes de aprobación"
                            subtitle="Acá aparecerán los graduados que necesiten ser aprobados para completar su registro"
                        />
                    </CustomPaper>
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            )}
            <CustomModal
                isOpen={isUserModalOpen}
                handleClose={() => setIsUserModalOpen(false)}
            >
                <div className="w-[65vw] md:w-[500px] max-h-[50vh] md:max-h-80">
                    <PendingUserForm
                        info={selectedUser as UserDTO}
                        handleClose={() => setIsUserModalOpen(false)}
                    />
                </div>
            </CustomModal>
            <CustomModal
                isOpen={isApproveUserOpen}
                handleClose={() => setIsApproveUserOpen(false)}
            >
                <TextModal
                    title={'Aprobar Graduado'}
                    content={'¿Estás seguro de que querés aprobar este graduado?'}
                    onAction={() => {
                        handleAction(true)
                    }}
                    onClose={() => setIsApproveUserOpen(false)}
                    loading={loading}
                />
            </CustomModal>
            <CustomModal
                isOpen={isRejectUserOpen}
                handleClose={() => setIsRejectUserOpen(false)}
            >
                <RejectModal
                    title={'Rechazar Graduado'}
                    content={'¿Por qué motivo querés rechazar este graduado?'}
                    onAction={(object) => handleAction(false, object)}
                    onClose={() => setIsRejectUserOpen(false)}
                    loading={loading}
                />
            </CustomModal>
        </div>
    )
}

export default GraduatesPage
